import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface GridState {
  gridKey: string;
  totalRows: number;
  selectedRows: number;
  filteredRows: number;
  isFullscreen: boolean;
  searchTerm: string;
  lastUpdated: string;
}

export interface AgGridState {
  grids: Record<string, GridState>;
  globalSettings: {
    defaultPageSize: number;
    enableAnimations: boolean;
    enableTooltips: boolean;
    autoSizeColumns: boolean;
  };
}

const initialState: AgGridState = {
  grids: {},
  globalSettings: {
    defaultPageSize: 50,
    enableAnimations: true,
    enableTooltips: true,
    autoSizeColumns: false,
  },
};

export const agGridSlice = createSlice({
  name: 'agGrid',
  initialState,
  reducers: {
    // Grid-specific actions
    setGridState: (state, action: PayloadAction<{ gridKey: string; gridState: Partial<GridState> }>) => {
      const { gridKey, gridState } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey] = {
        ...state.grids[gridKey],
        ...gridState,
        lastUpdated: new Date().toISOString(),
      };
    },

    setTotalRows: (state, action: PayloadAction<{ gridKey: string; totalRows: number }>) => {
      const { gridKey, totalRows } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey].totalRows = totalRows;
      state.grids[gridKey].lastUpdated = new Date().toISOString();
    },

    setSelectedRows: (state, action: PayloadAction<{ gridKey: string; selectedRows: number }>) => {
      const { gridKey, selectedRows } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey].selectedRows = selectedRows;
      state.grids[gridKey].lastUpdated = new Date().toISOString();
    },

    setFilteredRows: (state, action: PayloadAction<{ gridKey: string; filteredRows: number }>) => {
      const { gridKey, filteredRows } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey].filteredRows = filteredRows;
      state.grids[gridKey].lastUpdated = new Date().toISOString();
    },

    setFullscreen: (state, action: PayloadAction<{ gridKey: string; isFullscreen: boolean }>) => {
      const { gridKey, isFullscreen } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey].isFullscreen = isFullscreen;
      state.grids[gridKey].lastUpdated = new Date().toISOString();
    },

    setSearchTerm: (state, action: PayloadAction<{ gridKey: string; searchTerm: string }>) => {
      const { gridKey, searchTerm } = action.payload;
      if (!state.grids[gridKey]) {
        state.grids[gridKey] = {
          gridKey,
          totalRows: 0,
          selectedRows: 0,
          filteredRows: 0,
          isFullscreen: false,
          searchTerm: '',
          lastUpdated: new Date().toISOString(),
        };
      }
      state.grids[gridKey].searchTerm = searchTerm;
      state.grids[gridKey].lastUpdated = new Date().toISOString();
    },

    clearGridState: (state, action: PayloadAction<string>) => {
      const gridKey = action.payload;
      delete state.grids[gridKey];
    },

    // Global settings actions
    setGlobalSettings: (state, action: PayloadAction<Partial<AgGridState['globalSettings']>>) => {
      state.globalSettings = {
        ...state.globalSettings,
        ...action.payload,
      };
    },

    setDefaultPageSize: (state, action: PayloadAction<number>) => {
      state.globalSettings.defaultPageSize = action.payload;
    },

    setEnableAnimations: (state, action: PayloadAction<boolean>) => {
      state.globalSettings.enableAnimations = action.payload;
    },

    setEnableTooltips: (state, action: PayloadAction<boolean>) => {
      state.globalSettings.enableTooltips = action.payload;
    },

    setAutoSizeColumns: (state, action: PayloadAction<boolean>) => {
      state.globalSettings.autoSizeColumns = action.payload;
    },

    // Reset all grids
    resetAllGrids: (state) => {
      state.grids = {};
    },
  },
});

export const {
  setGridState,
  setTotalRows,
  setSelectedRows,
  setFilteredRows,
  setFullscreen,
  setSearchTerm,
  clearGridState,
  setGlobalSettings,
  setDefaultPageSize,
  setEnableAnimations,
  setEnableTooltips,
  setAutoSizeColumns,
  resetAllGrids,
} = agGridSlice.actions;

// Selectors
export const selectGridState = (state: { agGrid: AgGridState }, gridKey: string) => 
  state.agGrid.grids[gridKey];

export const selectAllGrids = (state: { agGrid: AgGridState }) => 
  state.agGrid.grids;

export const selectGlobalSettings = (state: { agGrid: AgGridState }) => 
  state.agGrid.globalSettings;

export const selectGridTotalRows = (state: { agGrid: AgGridState }, gridKey: string) => 
  state.agGrid.grids[gridKey]?.totalRows || 0;

export const selectGridSelectedRows = (state: { agGrid: AgGridState }, gridKey: string) => 
  state.agGrid.grids[gridKey]?.selectedRows || 0;

export const selectGridIsFullscreen = (state: { agGrid: AgGridState }, gridKey: string) => 
  state.agGrid.grids[gridKey]?.isFullscreen || false;

export const selectGridSearchTerm = (state: { agGrid: AgGridState }, gridKey: string) => 
  state.agGrid.grids[gridKey]?.searchTerm || '';

export default agGridSlice.reducer;